'use client';

import { useState, useMemo } from 'react';

import { ArrowRightIcon, PhoneIcon, WhatsappIcon } from '@/shared/components/icons';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Textarea } from '@/shadcn/components/ui/textarea';
import * as yup from 'yup';
import { validateMobilePhoneE164 } from '@/shared/validation/validators/validateMobilePhoneE164';
import { useParams } from 'next/navigation';
import { useFormik } from 'formik';
import FormInput from '@/shared/components/ui/FormInput';
import { getErrorText, omitEmptyFields } from '@/shared/utils/formik-utils';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shadcn/lib/utils';
import { PhoneCallIcon } from 'lucide-react';
import { MessageIcon } from '@/shared/components/icons';

export const schema = yup
    .object({
        advertPublicId: yup.string().required(),
        name: yup
            .string()
            .min(3, 'Meno musí obsahovať aspon 3 znaky')
            .max(100, 'Meno môže obsahovať maximálne 100 znaky')
            .required('Povinné pole'),
        email: yup.string().required('Povinné pole').email('Neprávny formát'),
        phone: yup
            .string()
            .test(
                'phone-e164',
                'Telefónne číslo nie je v platnom formáte (napr. +421 905 123 456)',
                function (value) {
                    if (!value) return true;
                    const res = validateMobilePhoneE164(value);
                    if (res.ok) return true;
                    return this.createError({
                        message: res.message,
                    });
                },
            )
            .optional(),
        message: yup
            .string()
            .min(5, 'Správa musí obsahovať minimálne 5 znaky')
            .required('Povinné pole'),
        token: yup.string().min(5).required(),
        consentAccepted: yup.boolean().oneOf([true], 'Musíte súhlasiť').required('Povinné pole'),
    })
    .strict(true);
export type Dto = yup.InferType<typeof schema>;

export function RealtorsContactForm() {
    const params = useParams();

    const [clickedOnSubmit, setClickedOnSubmit] = useState(false);
    const [errorResponse, setErrorResponse] = useState('');
    const [successResponse, setSuccessResponse] = useState('');
    const [loading, setLoading] = useState(false);

    const initialData = {
        advertPublicId: '123',
        name: '',
        phone: '',
        email: '',
        message:
            'Dobrý deň,\nmám záujem o inzerovanú nehnuteľnosť.\nProsím kontaktujte ma.\nĎakujem',
        consentAccepted: false,
        token: '',
    };

    const formik = useFormik<Dto>({
        validationSchema: schema,
        initialValues: initialData,
        onSubmit: async (values: Dto) => {
            const payload = omitEmptyFields(values);
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) return;

            try {
                setSuccessResponse('Formulár bol úspešne odoslaný.');
            } catch (e) {
                setErrorResponse('Formulár sa nepodarilo odoslať. Skúste to neskôr.');
            }
        },
    });

    const CONTACTS = useMemo(() => {
        return [
            {
                icon: <PhoneIcon className="w-[15px]" />,
            },
            {
                icon: <MessageIcon className="w-[15px] fill-[#000]" />,
            },
            {
                icon: <WhatsappIcon className="w-[15px]" />,
            },
        ];
    }, []);

    return (
        <div
            className={cn(
                // 'shadow-[0_5px_20px_0_rgba(0,0,0,0.09)] rounded-[20px] p-[20px] py-[30px] flex flex-col gap-[20px]',
                // 'sm:p[25px] md:p-[30px]',
                'p-[20px] py-[35px] bg-[#f9f9f9] rounded-[15px]',
                'lg:p-[25px] lg:py-[40px]',
                'xl:p-[30px] xl:py-[45px]',
            )}
        >
            <div className="">
                {/*<Img src="/images/image_placeholder.png" class />*/}
                <div>
                    <h4 className="font-[700] text-[18px] text-center xl:text-[20px]">Kontakt</h4>
                </div>
                <div className="flex flex-col gap-[3px]">
                    <h4 className="text-[15px] mt-[15px]">MAXIMA Real s.r.o</h4>
                    <ul>
                        <li className="text-[13px]">Medzilaborecka 18/99</li>
                        <li className="text-[13px]">040 01 Bratislava</li>
                    </ul>
                </div>
            </div>
            <div className="flex flex-col gap-[10px] mt-[20px] md:gap-[15px]">
                <div
                    className={cn(
                        'flex flex-col gap-[10px] md:grid md:grid-cols-2 md:gap-[15px]',
                        'lg:grid-cols-1',
                    )}
                >
                    <div className="flex flex-col gap-[10px] md:gap-[15px]">
                        <FormInput
                            id="name"
                            className="bg-white"
                            placeholder="Meno a priezvisko"
                            required
                            error={getErrorText(formik, 'name')}
                            value={formik.values.name}
                        />
                        <FormInput
                            id="email"
                            className="bg-white"
                            placeholder="Email"
                            type="email"
                            required
                            error={getErrorText(formik, 'email')}
                            value={formik.values.email}
                        />
                        <FormInput
                            id="phone"
                            className="bg-white"
                            placeholder="Telefón (nepovinné)"
                            type="tel"
                            error={getErrorText(formik, 'phone')}
                            value={formik.values.phone}
                        />
                    </div>
                    <div>
                        <Textarea
                            rows={5}
                            className="resize-none h-full p-[12px] text-[14px] leading-[130%] bg-white"
                            value={formik.values.message}
                            onChange={() => {}}
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-[15px] mt-[5px] md:grid md:grid-cols-2 lg:grid-cols-1">
                    <div className="flex items-start gap-[10px]">
                        <Checkbox id="consent" />
                        <label
                            htmlFor="consent"
                            className={cn(
                                '-mt-[1px] text-[11px] leading-[135%] text-[#868686] [&>a]:underline',
                                'xl:text-[12px]',
                            )}
                        >
                            Súhlasím so <a href="#">Všeobecnými podmienkami</a> a vyhlasujem, že som
                            sa oboznámil/a s informáciami o{' '}
                            <a href="#">spracúvaní osobných údajov</a>.
                        </label>
                    </div>

                    <div
                        className={cn(
                            'w-full flex flex-col items-center gap-[10px]',
                            'sm:flex-row sm:justify-between sm:gap-[15px] sm:mt-[15px]',
                        )}
                    >
                        <div className={cn('flex justify-end', 'sm:order-2')}>
                            <Button color="black" className="min-w-[120px] !text-[13px]">
                                Odoslať
                                <ArrowRightIcon />
                            </Button>
                        </div>
                        <div
                            className={cn(
                                'flex items-center justify-center w-full gap-[15px]',
                                'sm:order-1 sm:justify-start',
                            )}
                        >
                            {CONTACTS.map((contact, i) => {
                                return (
                                    <a
                                        key={i}
                                        href="#"
                                        className="size-[40px] flex items-center justify-center border rounded-full"
                                    >
                                        {contact.icon}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
